import React from 'react'
import { useResizeObserver } from '../../_hooks/ResizeObserver'
import { useCurrentMedia } from '../../_hooks/useCurrentMedia'

type ActionsPosition = 'top' | 'bottom'

const contentGap = 16

const pixelsToNumber = (value: string) => Number(value.slice(0, -2))

/**
 * Returns `true` if first block collides with second block in the container, `false` otherwise
 * */
const checkCollision = (
  container: HTMLElement,
  firstBlock: HTMLElement,
  secondBlock: HTMLElement
): boolean => {
  const containerWidth = pixelsToNumber(getComputedStyle(container).width)
  const firstBlockWidth = pixelsToNumber(getComputedStyle(firstBlock).width)
  const secondBlockWidth = pixelsToNumber(getComputedStyle(secondBlock).width)

  return firstBlockWidth + contentGap + secondBlockWidth >= containerWidth
}

export const usePageHeaderTemplateLayout = () => {
  const { isTabletLandscape, isDesktop } = useCurrentMedia()

  const headerRef = React.useRef<HTMLDivElement>(null)
  const breadcrumbsWrapperRef = React.useRef<HTMLDivElement>(null)
  const breadcrumbsRef = React.useRef<HTMLDivElement>(null)
  const actionsRef = React.useRef<HTMLDivElement>(null)
  const headingRef = React.useRef<HTMLDivElement>(null)

  const [actionsPosition, setActionsPosition] =
    React.useState<ActionsPosition>('top')

  const handleResize = React.useCallback(() => {
    const header = headerRef.current
    const breadcrumbsWrapper = breadcrumbsWrapperRef.current
    const breadcrumbs = breadcrumbsRef.current
    const actions = actionsRef.current

    if (!header || !actions) {
      return
    }

    if (!breadcrumbs || !breadcrumbsWrapper) {
      const heading = headingRef.current

      if (!heading) {
        return
      }

      if (isDesktop || isTabletLandscape) {
        if (actionsPosition === 'bottom') {
          setActionsPosition('top')
        }
        return
      }

      const hasCollision = checkCollision(header, heading, actions)

      if (actionsPosition === 'top' && hasCollision) {
        setActionsPosition('bottom')
        return
      }

      if (actionsPosition === 'bottom' && !hasCollision) {
        setActionsPosition('top')
      }

      return
    }

    const hasCollision = checkCollision(
      breadcrumbsWrapper,
      breadcrumbs,
      actions
    )

    if (actionsPosition === 'top' && hasCollision) {
      setActionsPosition('bottom')
      return
    }

    if (actionsPosition === 'bottom' && !hasCollision) {
      setActionsPosition('top')
    }
  }, [actionsPosition, isDesktop, isTabletLandscape])

  const observe = useResizeObserver(handleResize)

  React.useEffect(() => {
    observe(headerRef.current)
  }, [observe])

  return {
    headerRef,
    breadcrumbsWrapperRef,
    breadcrumbsRef,
    actionsRef,
    headingRef,

    actionsPosition,
  }
}
